import React, { useContext, useEffect } from "react";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { Box, Button, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { constructProgressEvent } from "../../../../../utils/constructProgressEvent";
import { useEventDispatcher } from "../../../../../utils/hooks/useEventDispatcher";
import Text from "../../../../shared/components/Text";
import { useActionCallback } from "../../../../../utils/hooks/useActionCallback";

export default function SignatureEmbedded({ onSuccess }) {
  const {
    state: { loan, signatureChannel, currentModuleObject, participant },
    dispatch,
  } = useContext(ProcessContext);

  const { palette } = useTheme();

  const { dispatchEvent } = useEventDispatcher();

  useEffect(() => {
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        hideSections: true,
      },
    });
    const progressEvent = constructProgressEvent(
      currentModuleObject?.id,
      "SIGNATURE_EMBEDDED"
    );
    dispatchEvent(progressEvent);
  }, []);

  useActionCallback(
    "REQUEST_PATCH_UPDATE_SIGNATURE_STATUS_FOR_LOAN_LENDER_BORROWER_CONTRACT_FULFILLED",
    () => checkSignatureSuccess()
  );

  const onSignatureCompleted = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
        loadingMessage: <Text tId="PLEASE_WAIT" />,
      },
    });

    dispatch({
      type: "REQUEST_PATCH_UPDATE_SIGNATURE_STATUS_FOR_LOAN_LENDER_BORROWER_CONTRACT",
      payload: loan.loan_application_selected.id,
    });
  };

  const isContractSigned =
    loan?.loan_application_selected?.loan_lender_borrower_contract?.is_signed;

  const checkSignatureSuccess = () => {
    if (!isContractSigned) {
      dispatch({
        type: "REQUEST_GET_LOAN",
        payload: loan.id,
      });
    } else {
      patchSyncBorrowerDocumentationByLoanId();
    }
  };

  const handleSuccessfulGetLoan = () => {
    if (isContractSigned) {
      patchSyncBorrowerDocumentationByLoanId();
    } else {
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: { show: false },
      });
      dispatch({
        type: "SET_FLOW_MESSAGE",
        payload: undefined,
      });
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: {
          snackbarNotification: {
            snackbarInformation: { type: "LOAN_NOT_SIGNED" },
            level: "warning",
          },
        },
      });
    }
  };

  const requestGetDocumentationRequired = () => {
    dispatch({
      type: "REQUEST_GET_DOCUMENTATION_REQUIRED",
      payload: {
        loanId: loan.id,
        borrowerId: participant.participantId,
      },
    });
  };

  const patchSyncBorrowerDocumentationByLoanId = () => {
    dispatch({
      type: "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID",
      payload: {
        op: "processPatchSyncBorrowerDocumentationByLoanId",
        id: loan.id,
      },
    });
  };

  useActionCallback(
    "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID_FULFILLED",
    () => {
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: {
          documentationSyncedAfterSigning: true,
        },
      });
      requestGetDocumentationRequired();
    }
  );

  useActionCallback("REQUEST_GET_DOCUMENTATION_REQUIRED_FULFILLED", () =>
    handleSuccess()
  );

  const handleSuccess = () => {
    onSuccess();
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        hideSections: false,
      },
    });
  };

  useActionCallback("REQUEST_GET_LOAN_FULFILLED", () =>
    handleSuccessfulGetLoan()
  );

  const iframeStyle = {
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    border: 0,
  };

  return (
    <Box
      className="appear-anim"
      style={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gridGap: "16px",
      }}
    >
      <Box position={"relative"} flexGrow={1}>
        <iframe
          align="center"
          name="iFrameLgt"
          id="iFrameLgt"
          style={iframeStyle}
          src={signatureChannel?.url}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={"column"}
        alignItems="flex-end"
        justifyContent={"space-between"}
        p={2}
        style={{
          gridGap: "8px",
          backgroundColor: palette.info.main || "#f5f5f5",
        }}
      >
        <Box width="100%">
          <Typography
            variant={"caption"}
            display={"block"}
            style={{ color: palette.info.contrastText }}
          >
            <strong>
              <Text tId={"IMPORTANT"} />
            </strong>
          </Typography>
          <Typography
            variant={"caption"}
            display={"block"}
            style={{ color: palette.info.contrastText }}
          >
            <Text tId={"CLICK_FINALIZE_AFTER_SIGNATURE"} />
          </Typography>
        </Box>
        <Button
          size={"small"}
          color={"secondary"}
          variant={"contained"}
          onClick={onSignatureCompleted}
          endIcon={
            <i className="material-icons-outlined" style={{ fontSize: "18px" }}>
              check_circle
            </i>
          }
        >
          <Text tId={"FINALIZE"} />
        </Button>
      </Box>
    </Box>
  );
}
